import { ThunkAction, StoreShape } from 'hellospa/redux/types';
import * as selectors from '../selectors';
import {
  Actions,
  PrepAndSendAction,
  UserFileTypes,
  FilePollResponseStatus,
  BulkSendData,
} from 'hellospa/page/prep-and-send/data/types';
import { SendRequestResponse } from 'hellospa/app-context/actions/prep-and-send';
import capture from './capture';

export const initBulkSend = (): ThunkAction<Promise<null>> => async (
  dispatch, getState, getExtra,
) => {
  const { appActions } = getExtra();
  const cacheKey = selectors.getTransmissionGroupGuid(getState());
  const data = await appActions.initBulkSend(cacheKey);
  const document = selectors.getDocument(getState());
  let bulkSendData;
  if (data.form) {
    bulkSendData = {
      title: data.form.lockedTitle || document.title,
      message: data.form.lockedMessage || document.message,
    };
    dispatch({
      type: Actions.UpdateDocument,
      payload: bulkSendData,
      meta: {
        embeddedDocumentWasUpdated: false,
      },
    });
  }

  return null;
};

export const getBulkSendExample = (): ThunkAction<Promise<void>> => async (
  dispatch, getState, getExtra,
) => {
  const { appActions } = getExtra();
  const templates = selectors.getTemplates(getState());

  if (templates && templates.length > 0) {
    const data = await appActions.getBulkSendExample(templates);
    dispatch({
      type: Actions.SetBulkSendData,
      payload: {
        exampleFields: data.fields,
      },
    });
  }
};

export const validateBulkSendSigners = (file: File): ThunkAction<Promise<null|string>> => async (
  dispatch, getState, getExtra,
) => {
  const { appActions } = getExtra();
  const signerKey = selectors.getTransmissionGroupGuid(getState());
  const templates = selectors.getTemplates(getState());
  const templateIds = templates.map((template) => template.templateGuid);

  if (templates && templates.length > 0) {
    const error = await appActions.validateBulkSendData(
      file,
      signerKey,
      templateIds,
    );
    if (error) {
      return error.error;
    }
  }
  return null;
};

export const saveSignerCSV = (
  file: File,
): PrepAndSendAction => {
  return {
    type: Actions.SetBulkSendData,
    payload: {
      signerFile: {
        name: file.name,
        type: UserFileTypes.Signer,
        status: FilePollResponseStatus.Converting,
        progress: 0,
      },
    },
  };
};

export const markSignerCSVComplete = (
  file: File,
) => {
  const signerFile = {
    name: file.name,
    type: UserFileTypes.Signer,
    status: FilePollResponseStatus.Ok,
    progress: 1,
  };
  return {
    type: Actions.SetBulkSendData,
    payload: {
      signerFile,
    },
  };
};

export const deleteSignerCSV = (): PrepAndSendAction => ({
  type: Actions.SetBulkSendData,
  payload: {
    signerFile: undefined,
  },
});

export const submitBulkSendRequest = (): ThunkAction<Promise<SendRequestResponse>> => async (
  dispatch, getState, getExtra,
) => {
  const { appActions } = getExtra();
  const signerKey = selectors.getTransmissionGroupGuid(getState());
  const document = selectors.getDocument(getState());
  const title = document.title;
  const message = document.message;
  try {
    const redirectUrl = await appActions.submitBulkSendRequest(
      signerKey,
      title,
      message,
    );
    return {
      success: true,
      redirectUrl,
    };
  } catch (error) {
    return {
      errors: [error.message],
    };
  }
};

export const validateSignerFile = async (
  state: StoreShape,
): Promise<boolean> => {
  const schema = selectors.getRecipientValidationSchema(state);
  const data = {
    signerFile: selectors.getBulkSendData(state).signerFile,
  };
  if (!schema) {
    return true;
  }

  const errors = await capture(schema, data);

  return errors.length === 0;
};

export const setBulkSendData = (payload: Partial<BulkSendData>): PrepAndSendAction => ({
  type: Actions.SetBulkSendData,
  payload,
});
